// Phone/callFormat — pure helpers for the Calls page (unit-tested in callFormat.test.js).
// Every field name is lane C's /comms/calls/ contract (CallSerializer), not a guess:
//   direction(inbound|outbound) · outcome(answered|no_answer|voicemail|failed|busy) ·
//   duration_seconds · created_at · user / user_name · has_recording (list rows) /
//   recording_playback_url (detail read only — a list never presigns) /
//   recording_sid · voicemail · transcript / transcript_available · coaching (leg 4:
//   who, what_they_do, objection, objection_words, reply, better_reply, talk_ratio,
//   asked_for_next, do_differently) · customer{id,full_name,universal_id} ·
//   lead{id,name,universal_id} · parent_call_sid · handled_at / handled_by_name.

export const isChildLeg = (c) => Boolean(c && c.parent_call_sid);

export const contactName = (call) =>
  call?.customer?.full_name ||
  call?.customer?.name ||
  [call?.customer?.first_name, call?.customer?.last_name].filter(Boolean).join(" ") ||
  call?.lead?.full_name ||
  call?.lead?.name ||
  [call?.lead?.first_name, call?.lead?.last_name].filter(Boolean).join(" ") ||
  "";

// C's direction-resolved counterparty, else the raw pair by direction.
export const counterpartyNumber = (call) =>
  call?.counterparty_number ||
  call?.counterparty_digits ||
  (call?.direction === "inbound" ? call?.from_number : call?.to_number) ||
  call?.to_number ||
  call?.from_number ||
  "";

// The displayed outcome is the (outcome, direction) PAIR: a missed inbound and an
// unanswered outbound share outcome="no_answer" and are told apart by direction.
// "Missed" is a render label, never a server value. tone = Badge tone.
export const outcomeInfo = (call) => {
  const o = String(call?.outcome || "").toLowerCase().replace(/-/g, "_");
  const inbound = call?.direction === "inbound";
  if (o === "answered" || o === "completed" || o === "connected") return { key: "answered", label: "Answered", tone: "success" };
  if (o === "voicemail" || call?.voicemail) return { key: "voicemail", label: "Voicemail", tone: "danger" };
  if (o === "no_answer") return inbound ? { key: "missed", label: "Missed", tone: "danger" } : { key: "no_answer", label: "No answer", tone: "warning" };
  if (o === "busy") return { key: "busy", label: "Busy", tone: "warning" };
  if (o === "failed") return { key: "failed", label: "Failed", tone: "danger" };
  // Unknown/legacy status with real talk time reads as answered.
  if (Number(call?.duration_seconds) > 0) return { key: "answered", label: "Answered", tone: "success" };
  if (o) return { key: o, label: o.charAt(0).toUpperCase() + o.slice(1).replace(/_/g, " "), tone: "neutral" };
  return { key: "unknown", label: "—", tone: "neutral" };
};

export const isMissed = (call) => outcomeInfo(call).key === "missed";
export const isVoicemail = (call) => outcomeInfo(call).key === "voicemail";

// The serializer's user_name is "Customer" when no user is attached — that is not a
// rep. null means "unknown", which renders as "—", never as a name.
export const repName = (call) => {
  if (!call?.user) return null;
  const n = String(call.user_name || "").trim();
  return n && n !== "Customer" ? n : null;
};

// A list row answers with `has_recording` (no presign); a detail read carries the
// presigned recording_playback_url; recording_sid alone means Twilio has audio we
// have not stored yet. Older rows (before has_recording existed) still carry a URL.
export const hasRecording = (c) => Boolean(c?.has_recording || c?.recording_playback_url || c?.recording_sid);
export const recordingProcessing = (c) =>
  Boolean(c?.recording_sid) && !c?.has_recording && !c?.recording_playback_url;
// Something to play that must be fetched on Play (GET /comms/calls/<sid>/recording-url/).
export const recordingNeedsFetch = (c) => Boolean(c?.has_recording && !c?.recording_playback_url && c?.call_sid);
export const hasTranscript = (c) => Boolean(c?.transcript_available && String(c?.transcript || "").trim());
export const hasCoaching = (c) => {
  const card = c?.coaching;
  if (!card || typeof card !== "object") return false;
  return Object.values(card).some((v) => {
    if (v === null || v === undefined || v === "") return false;
    if (typeof v === "object") return Object.keys(v).length > 0;
    return true;
  });
};

// m:ss, tabular. null/undefined = unknown → "—". 0 is a real zero (a missed call).
export const fmtDuration = (secs) => {
  if (secs === null || secs === undefined || secs === "") return "—";
  const s = Math.max(0, Math.floor(Number(secs)));
  if (Number.isNaN(s)) return "—";
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

// Today → "10:42 AM"; earlier → "Oct 21, 10:42 AM"; another year → "Oct 21, 2025".
export const fmtWhen = (iso, now = new Date()) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const time = d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  if (sameDay(d, now)) return time;
  if (d.getFullYear() === now.getFullYear()) return `${d.toLocaleDateString([], { month: "short", day: "numeric" })}, ${time}`;
  return d.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });
};

export const fmtFull = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString([], { dateStyle: "medium", timeStyle: "short" });
};

// The call record's transcript is "Speaker: text" lines (C's Transcript.as_text and
// the voice service's chunk join). Lines without a label continue the previous
// speaker. Speaker labels: Rep / Caller from Transcribe channels; legacy AI / Caller.
const LINE_RE = /^([A-Za-z][\w .'-]{0,24}?):\s+(.*)$/;
export const parseTranscript = (text) => {
  const out = [];
  String(text || "")
    .split(/\r?\n/)
    .forEach((raw) => {
      const line = raw.trim();
      if (!line) return;
      const m = line.match(LINE_RE);
      if (m) {
        out.push({ speaker: m[1].trim(), text: m[2].trim() });
      } else if (out.length) {
        out[out.length - 1].text += `\n${line}`;
      } else {
        out.push({ speaker: "", text: line });
      }
    });
  return out;
};

const SPEAKER_LABELS = { rep: "Rep", agent: "Rep", caller: "Caller", customer: "Caller", ai: "Assistant", assistant: "Assistant" };
export const speakerLabel = (s) => SPEAKER_LABELS[String(s || "").toLowerCase()] || s || "";
export const speakerKind = (s) => {
  const k = String(s || "").toLowerCase();
  if (k === "rep" || k === "agent") return "rep";
  if (k === "ai" || k === "assistant") return "ai";
  return k ? "caller" : "";
};

// Stat tiles for today. Unknown is never 0: a null means "—" + a reason.
export const todayStats = (rows, now = new Date()) => {
  const today = (rows || []).filter((c) => !isChildLeg(c)).filter((c) => {
    const d = new Date(c.created_at || 0);
    return !Number.isNaN(d.getTime()) && sameDay(d, now);
  });
  const n = today.length;
  const answered = today.filter((c) => outcomeInfo(c).key === "answered");
  const missed = today.filter(isMissed).length;
  const talked = answered.map((c) => Number(c.duration_seconds)).filter((s) => Number.isFinite(s) && s > 0);
  return {
    calls: n,
    answered: answered.length,
    answeredPct: n ? Math.round((answered.length / n) * 100) : null,
    missed,
    avgDuration: talked.length ? Math.round(talked.reduce((a, b) => a + b, 0) / talked.length) : null,
  };
};

// D-036 class: Save sends only what changed / was filled, never the whole form.
export const wrapUpPayload = ({ call, outcome, notes, followUpDate }) => {
  const payload = {};
  const o = String(outcome || "");
  if (o && o !== String(call?.outcome || "")) payload.outcome = o;
  const n = String(notes || "").trim();
  if (n) payload.notes = n;
  if (followUpDate) payload.next_action = { type: "task", title: "Call follow-up", due_date: followUpDate };
  return Object.keys(payload).length ? payload : null;
};

const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const endOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);

// ?start=&end= for the list view (ISO, local day boundaries).
export const dateRangeParams = (range, from, to, now = new Date()) => {
  const sod = startOfDay(now);
  const days = (n) => new Date(sod.getTime() - n * 86400000);
  switch (range) {
    case "today": return { start: sod.toISOString() };
    case "yesterday": return { start: days(1).toISOString(), end: new Date(sod.getTime() - 1).toISOString() };
    case "7d": return { start: days(6).toISOString() };
    case "30d": return { start: days(29).toISOString() };
    case "custom": {
      const out = {};
      if (from) { const d = new Date(`${from}T00:00:00`); if (!Number.isNaN(d.getTime())) out.start = d.toISOString(); }
      if (to) { const d = new Date(`${to}T00:00:00`); if (!Number.isNaN(d.getTime())) out.end = endOfDay(d).toISOString(); }
      return out;
    }
    default: return {};
  }
};

export const matchesQuery = (call, q) => {
  const s = String(q || "").trim().toLowerCase();
  if (!s) return true;
  const digits = s.replace(/\D/g, "");
  const name = contactName(call).toLowerCase();
  const num = counterpartyNumber(call);
  if (name.includes(s)) return true;
  if (digits && String(num).replace(/\D/g, "").includes(digits)) return true;
  return String(num).toLowerCase().includes(s);
};
